import React from "react";
import type { match } from "react-router-dom";
import { useHistory, Link } from "react-router-dom";
import IntegrationNameAndLogo from "../../../shared/IntegrationNameAndLogo";
import { HTTPMethodBadge, ResponseCodeBadge } from "../../../shared/MergeBadges";
import type { APIRequestLogEntry } from "../../IntegrationsManagementEntities";
import DirectionCell from "../shared/DirectionCell";
import { LinkedAccount } from "../../../../models/Entities";
import { formatDate } from "../../../../models/Helpers";
import { Tooltip, Text } from "@merge-api/merge-javascript-shared";
import { ChevronRight } from "lucide-react";

interface Props {
  log: APIRequestLogEntry;
  match: match;
  linkedAccount?: LinkedAccount | null;
  /**
   * Called when the route is pushed when clicking this row
   */
  onPush?: () => void;
}

const LogEntryLink = (props: React.PropsWithChildren<{ logId: string; match: match }>) => {
  return (
    <Link
      onClick={(e) => e.stopPropagation()}
      to={{
        pathname: `${props.match.path}/${props.logId}`,
        state: { from: window.location.pathname },
      }}
      className="block w-full text-black"
    >
      {props.children}
    </Link>
  );
};

/**
 * Creates one table row for a log entry
 */
const LogEntryRow = ({ log, match, onPush, linkedAccount }: Props) => {
  const history = useHistory();

  const navigateToLogEntrySidePanel = (logId: string) => {
    onPush && onPush();
    history.push(`${match.path}/${logId}`, { from: window.location.pathname });
  };

  const methodCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        {log.method === "FETCH" ? (
          <Tooltip title="FETCH indicates that data was pulled via a method other than HTTP request">
            <span className="truncate">
              <HTTPMethodBadge method={log.method} />
            </span>
          </Tooltip>
        ) : (
          <HTTPMethodBadge method={log.method} />
        )}
      </LogEntryLink>
    </td>
  );

  // URL column absorbs the table's leftover horizontal space (w-full + max-w-0)
  // so it uses whatever room the screen gives it, with a min-width floor so it
  // never collapses to nothing on a crowded row.
  // URL cell can contain third-party request URLs with end-user identifiers in
  // query params and is thus masked at the cell (Tooltip body is portal-mounted
  // and covered by maskTextSelector).
  const urlCell = (
    <td
      className="ph-no-capture w-full min-w-[50px] max-w-0 md:min-w-[100px]"
      onClick={() => navigateToLogEntrySidePanel(log.id)}
    >
      <LogEntryLink logId={log.id} match={match}>
        {log.url && (
          <Tooltip title={log.url} className="flex w-full min-w-0 items-center truncate">
            <Text className="min-w-0 truncate">{log.url}</Text>
          </Tooltip>
        )}
      </LogEntryLink>
    </td>
  );

  const directionCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        <div className="flex">
          <DirectionCell log={log} />
        </div>
      </LogEntryLink>
    </td>
  );

  const statusCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        <ResponseCodeBadge responseCode={log.response_code} />
      </LogEntryLink>
    </td>
  );

  const integrationCell = !linkedAccount ? (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        {log.linked_account?.integration && (
          <IntegrationNameAndLogo integration={log.linked_account.integration} />
        )}
      </LogEntryLink>
    </td>
  ) : null;

  const userCell = !linkedAccount ? (
    // Organization name cell is customer-chosen and is thus masked at the cell
    <td className="ph-no-capture" onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        <Text>{log.linked_account?.end_user?.organization_name}</Text>
      </LogEntryLink>
    </td>
  ) : null;

  const dateCell = (
    <td onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        <Text className="text-gray-40">{formatDate(log.created_at)}</Text>
      </LogEntryLink>
    </td>
  );

  const chevronCell = (
    <td className="text-right" onClick={() => navigateToLogEntrySidePanel(log.id)}>
      <LogEntryLink logId={log.id} match={match}>
        <Tooltip title="Review log details">
          <ChevronRight size={16} className="text-gray-40" />
        </Tooltip>
      </LogEntryLink>
    </td>
  );

  return (
    <tr className="table-link">
      {methodCell}
      {urlCell}
      {directionCell}
      {statusCell}
      {integrationCell}
      {userCell}
      {dateCell}
      {chevronCell}
    </tr>
  );
};

export default LogEntryRow;
